import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import { Box, FormGroup, Grid2 as Grid, Switch, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material';
import React, { useContext, useEffect, useRef } from 'react';
import IsMountedWrapper from '../../../utils/functions/isMountedWrapper';
import { Product } from '../../../utils/models/products';
import { BookingStore } from '../../../utils/stores/BookingStore';
import { ProductsStore } from '../../../utils/stores/ProductsStore';
import { ExtraSelectionContainer } from './ExtraSelectionStep';
import { commonStyles } from './commonStyles';

interface PickUpListProps {
    product: Product;
}

interface PackageSelectProps {
    readonly isPackage?: boolean;
}

export default function SelectPickUp({ isPackage }: PackageSelectProps) {
    const theme = useTheme();
    const isMounted = IsMountedWrapper();
    const productController = useContext(ProductsStore);
    const bookingController = useContext(BookingStore);
    const product = isPackage ? productController.packageProduct.data : productController.product;
    const booking = isPackage ? bookingController.packageBooking.data : bookingController.booking;
    const extra = product?.extras.find((e) => e.id === 'Bus Transfer');
    const pickUpExists = product?.pickupList && product.pickupList.slice(1, product.pickupList.length).length > 0;
    const needPickUp = isPackage ? bookingController.needPackagePickUp.data : bookingController.needPickUp.data;
    const locationSelected = booking?.tour?.pickUpLocation !== product?.pickupList?.[0]?.locationName;
    const locationSelectedRef = useRef<boolean>();
    locationSelectedRef.current = locationSelected;

    const checkPickUp = () => {
        const defaultLocationName = product?.pickupList?.[0]?.locationName;
        if (!locationSelectedRef.current && defaultLocationName) {
            !isPackage
                ? bookingController.changePickUp(null, defaultLocationName)
                : bookingController.changePackagePickUp(null, defaultLocationName);
        }
    };

    useEffect(() => {
        const pickUpStatus = booking?.tour?.pickUpLocation !== product?.pickupList?.[0]?.locationName;
        !isPackage
            ? bookingController.needPickUp.setData(pickUpStatus, isMounted)
            : bookingController.needPackagePickUp.setData(pickUpStatus, isMounted);
        return () => {
            checkPickUp();
        };
    }, []);

    useEffect(() => {
        locationSelectedRef.current = locationSelected;
    }, [booking.tour.pickUpLocation]);

    const PickUpList = ({ product }: PickUpListProps) => {
        return (
            <>
                {product.pickupList.slice(1, product.pickupList.length).map((location) => (
                    <option key={location.locationName} value={location.locationName}>
                        {location.locationName}
                    </option>
                ))}
            </>
        );
    };

    return (
        <>
            {pickUpExists && (
                <Grid container style={{ marginBottom: 10 }}>
                    <Grid size={{ xs: 'auto' }}>
                        <FormGroup row sx={commonStyles.pickUpCheckBox}>
                            <Typography sx={commonStyles.subtitle} style={{ padding: '0 10px 0 0' }}>
                                Need a Ride?
                            </Typography>
                            <Switch
                                color="primary"
                                checked={needPickUp}
                                onChange={
                                    isPackage ? bookingController.togglePackagePickUp : bookingController.togglePickUp
                                }
                                name="selectPickUp"
                            />
                        </FormGroup>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 'grow' }} style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={commonStyles.checkLabel}>Check here to select a pick-up</Typography>
                    </Grid>
                    {!extra && (
                        <Typography
                            component="div"
                            sx={[commonStyles.fieldInputSection, commonStyles.checkLabel]}
                            style={{ opacity: needPickUp ? 1 : 0.4, marginTop: 10 }}
                        >
                            <Box display="inline" style={{ color: theme.palette.primary.main, fontWeight: 400 }}>
                                Oooh yeah!
                            </Box>{' '}
                            Your booking includes a free transfer service. Just select the location below, and
                            we&apos;ll give you a lift to and from your tour.
                        </Typography>
                    )}
                </Grid>
            )}
            {extra && (
                <Box style={{ opacity: needPickUp ? 1 : 0.4, marginTop: 20 }}>
                    <ExtraSelectionContainer
                        extra={extra}
                        fixQuantity={
                            isPackage
                                ? bookingController.pickUpQuantities.packageQuantity
                                : bookingController.pickUpQuantities.quantity
                        }
                    />
                </Box>
            )}
            {pickUpExists && (
                <Grid size={{ xs: 12 }} sx={commonStyles.fieldInputSection} style={{ paddingLeft: extra && 0 }}>
                    <TextField
                        sx={commonStyles.fieldInputItem}
                        select
                        label="Select Location"
                        value={booking.tour.pickUpLocation || ''}
                        onChange={isPackage ? bookingController.changePackagePickUp : bookingController.changePickUp}
                        disabled={!needPickUp}
                        name="pickupLocation"
                        SelectProps={{
                            native: true,
                        }}
                        variant="outlined"
                    >
                        <option value="" />
                        <PickUpList product={product} />
                    </TextField>
                </Grid>
            )}
            {pickUpExists ? (
                <Grid size={{ xs: 12 }} sx={commonStyles.fieldInputSection} style={{ paddingLeft: extra && 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Meeting Point
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '24px minmax(0, 1fr)',
                            columnGap: 1,
                            rowGap: 1,
                            alignItems: 'start',
                        }}
                    >
                        <LocationOnRoundedIcon color="primary" sx={{ fontSize: 21, mt: '2px' }} />
                        <Typography variant="body1" sx={commonStyles.location}>
                            {booking.tour.pickUpLocation || 'TBA'}
                        </Typography>
                        <AccessTimeRoundedIcon color="primary" sx={{ fontSize: 21, mt: '2px' }} />
                        <Typography variant="body1" sx={commonStyles.location}>
                            {bookingController.calcDisplayTime(product, isPackage) || 'TBA'}
                        </Typography>
                    </Box>
                </Grid>
            ) : (
                <Grid size={{ xs: 12 }} sx={commonStyles.fieldInputSection}>
                    <Typography
                        variant="body1"
                        sx={commonStyles.subtitle}
                        style={{ paddingBottom: 5, fontWeight: 400 }}
                    >
                        {booking.tour.pickUpLocation}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={commonStyles.location}
                        style={{ paddingBottom: 20, fontWeight: 300 }}
                    >
                        {
                            product?.pickupList?.find((loc) => loc.locationName === booking.tour.pickUpLocation)
                                ?.additionalInstructions
                        }
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={commonStyles.subtitle}
                        style={{ paddingBottom: 20, fontWeight: 400 }}
                    >
                        Check In By: {bookingController.calcDisplayTime(product, isPackage) || 'TBA'}
                    </Typography>
                    <Typography variant="body1" sx={commonStyles.location} style={{ paddingBottom: 0 }}>
                        That&apos;s it, we&apos;ll see you there! This information will also be included with your
                        booking confirmation.
                    </Typography>
                </Grid>
            )}
        </>
    );
}
